import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { countOf } from '../../game/inventory';
import { ITEMS, RARITY_COLOR } from '../../game/items';
import { openPanel } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { roomFor } from './bag';
import { unreadCount } from './mail';
import {
  GOODS,
  MARKET_CATEGORIES,
  MAX_PLAYER_LISTINGS,
  SELL_CEILING,
  type PlayerListing,
  type TradeGood,
  bidPrice,
  buyFromMarket,
  cancelListing,
  listOnMarket,
  listingPayout,
  listingStatus,
  midPrice,
  orderBook,
  playerListings,
  priceBounds,
  priceHistory,
  priceSeries,
  quoteBuy,
  refreshMarket,
  searchGoods,
  sellDelaySec,
  sellToMarket,
} from './market';
import {
  Amount,
  Cell,
  Empty,
  Stepper,
  TipLayer,
  Wallet,
  commit,
  duration,
  fmt,
  toast,
  useCtx,
  useNow,
} from './ui';

type Tab = 'browse' | 'sell' | 'listings';
type Mode = 'buy' | 'sell' | 'list';

const nameOf = (id: string) => ITEMS[id]?.name ?? id;

function Sparkline({ values, w = 300, h = 70 }: { values: number[]; w?: number; h?: number }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const pts = values.map((v, i): [number, number] => [
    4 + (i / (values.length - 1)) * (w - 12),
    h - 8 - ((v - min) / span) * (h - 18),
  ]);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const first = pts[0] ?? [0, 0];
  const last = pts[pts.length - 1] ?? [0, 0];
  return (
    <svg
      class="mk-spark"
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      aria-label="14-day price history"
    >
      <defs>
        <linearGradient id="mk-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stop-color="#eacd95" stop-opacity="0.32" />
          <stop offset="1" stop-color="#eacd95" stop-opacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1="0" x2={w} y1={h * f} y2={h * f} stroke="#ffffff10" stroke-width="1" />
      ))}
      <path d={`${d} L${last[0]},${h} L${first[0]},${h} Z`} fill="url(#mk-fill)" />
      <path d={d} fill="none" stroke="#eacd95" stroke-width="1.7" stroke-linejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3.2" fill="#fae4b2" stroke="#3b2f14" />
    </svg>
  );
}

function Delta({ now, before }: { now: number; before: number }) {
  const d = before > 0 ? (now - before) / before : 0;
  if (Math.abs(d) < 0.005) return <span class="mk-delta">—</span>;
  return (
    <span
      class={`mk-delta ${d > 0 ? 'is-up' : 'is-down'}`}
    >{`${d > 0 ? '▲' : '▼'} ${Math.abs(d * 100).toFixed(1)}%`}</span>
  );
}

function Detail({ id, initialMode }: { id: string; initialMode: Mode }) {
  const ctx = useCtx();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState<number | null>(null);
  useEffect(() => setMode(initialMode), [initialMode]);
  if (!ctx) return null;
  const { p, c, a } = ctx;
  const g = GOODS[id] as TradeGood;
  const def = ITEMS[id];
  if (!g || !def) return null;
  const day = a.day;
  const mid = midPrice(id, day);
  const hist = priceHistory(id, day, 14);
  const book = orderBook(a, id);
  const supply = book.filter((l) => !l.player).reduce((s, l) => s + l.count, 0);
  const owned = countOf(c, id);
  const ownedBundles = Math.floor(owned / g.bundle);
  const [lo, hi] = priceBounds(id, day);
  const listPrice = Math.min(hi, Math.max(lo, price ?? mid));
  const nListings = playerListings(a).length;

  let body: JSX.Element;
  if (mode === 'buy') {
    const n = Math.max(1, Math.min(qty, Math.max(1, supply)));
    const q = quoteBuy(a, id, n);
    const block =
      supply <= 0
        ? 'Sold out today'
        : q.cost > a.wallet.gold
          ? 'Not enough gold'
          : roomFor(c, id) < q.units
            ? 'Bag is full'
            : null;
    body = (
      <>
        <div class="mk-form-row">
          <span>Bundles</span>
          <Stepper value={n} max={Math.max(1, Math.min(supply, 999))} onChange={setQty} />
        </div>
        <div class="mk-kv">
          <span>You receive</span>
          <b>
            {fmt(q.units)} {def.name}
          </b>
        </div>
        <div class="mk-kv mk-kv--total">
          <span>Total cost</span>
          <Amount kind="gold" n={q.cost} bad={q.cost > a.wallet.gold} />
        </div>
        <button
          class="btn btn--gold wide"
          disabled={!!block}
          onClick={() => {
            const r = buyFromMarket(p, id, n);
            if (!r.ok) return toast(r.reason, '#e5484d');
            toast(`Bought ${def.name} ×${fmt(r.units)} for ${fmt(-r.gold)} gold`, '#f1c33f');
            setQty(1);
            commit();
          }}
        >
          {block ?? 'Buy'}
        </button>
      </>
    );
  } else if (mode === 'sell') {
    const n = Math.max(1, Math.min(qty, Math.max(1, ownedBundles)));
    const bid = bidPrice(id, day);
    body = (
      <>
        <div class="mk-form-row">
          <span>Bundles</span>
          <Stepper value={n} max={Math.max(1, ownedBundles)} onChange={setQty} />
        </div>
        <div class="mk-kv">
          <span>Buy orders pay</span>
          <span>
            <Amount kind="gold" n={bid} /> <span class="fx-dim">per bundle</span>
          </span>
        </div>
        <div class="mk-kv mk-kv--total">
          <span>You receive now</span>
          <Amount kind="gold" n={bid * n} />
        </div>
        <button
          class="btn btn--gold wide"
          disabled={ownedBundles < 1}
          onClick={() => {
            const r = sellToMarket(p, id, n);
            if (!r.ok) return toast(r.reason, '#e5484d');
            toast(`Sold ${def.name} ×${fmt(r.units)} for ${fmt(r.gold)} gold`, '#f1c33f');
            setQty(1);
            commit();
          }}
        >
          {ownedBundles < 1 ? `Need ${g.bundle} to sell a bundle` : 'Sell now'}
        </button>
        <p class="mk-note">
          Instant, but buy orders pay about 10% under the market. Listing earns more if you can
          wait.
        </p>
      </>
    );
  } else {
    const n = Math.max(1, Math.min(qty, Math.max(1, ownedBundles)));
    const pay = listingPayout(listPrice, n);
    const tooHigh = listPrice > Math.ceil(mid * SELL_CEILING);
    const block =
      ownedBundles < 1
        ? `Need ${g.bundle} to list a bundle`
        : nListings >= MAX_PLAYER_LISTINGS
          ? `${MAX_PLAYER_LISTINGS} listings max`
          : null;
    body = (
      <>
        <div class="mk-form-row">
          <span>Bundles</span>
          <Stepper value={n} max={Math.max(1, ownedBundles)} onChange={setQty} />
        </div>
        <div class="mk-form-row">
          <span>Price each</span>
          <span class="mk-price-in">
            <Stepper value={listPrice} min={lo} max={hi} onChange={setPrice} />
            <button class="btn sm" onClick={() => setPrice(mid)} title="Match the market price">
              Market
            </button>
          </span>
        </div>
        <div class="mk-kv">
          <span>Sale value</span>
          <Amount kind="gold" n={pay.gross} />
        </div>
        <div class="mk-kv">
          <span>Broker's fee (5%)</span>
          <span class="mk-fee">−{fmt(pay.fee)}</span>
        </div>
        <div class="mk-kv mk-kv--total">
          <span>You receive by mail</span>
          <Amount kind="gold" n={pay.net} />
        </div>
        <div class={`mk-eta${tooHigh ? ' is-warn' : ''}`}>
          {tooHigh
            ? 'Buyers will not pay this much today. It waits until prices rise.'
            : `A buyer should appear in about ${duration(sellDelaySec(listPrice, mid) * 1000)}.`}
        </div>
        <button
          class="btn btn--gold wide"
          disabled={!!block}
          onClick={() => {
            const r = listOnMarket(p, id, n, listPrice);
            if (!r.ok) return toast(r.reason, '#e5484d');
            toast(
              `Listed ${def.name} ×${fmt(n * g.bundle)} at ${fmt(r.listing.price)} gold`,
              '#f1c33f',
            );
            setQty(1);
            commit();
          }}
        >
          {block ?? 'List for sale'}
        </button>
      </>
    );
  }

  return (
    <div class="mk-detail-in">
      <div class="mk-head">
        <Cell item={id} size={52} />
        <div>
          <div class="mk-title" style={{ color: RARITY_COLOR[def.rarity] }}>
            {def.name}
          </div>
          <div class="fx-sub">
            Bundle of {fmt(g.bundle)} · you own <b class="fx-num">{fmt(owned)}</b>
          </div>
        </div>
      </div>
      <div class="mk-chart">
        <div class="mk-chart-top">
          <span class="fx-h">14-day price</span>
          <span class="mk-now">
            <Amount kind="gold" n={mid} /> <Delta now={mid} before={midPrice(id, day - 1)} />
          </span>
        </div>
        <Sparkline values={priceSeries(id, day, 14)} />
        <div class="mk-chart-foot">
          <span>Low {fmt(Math.min(...hist))}</span>
          <span>High {fmt(Math.max(...hist))}</span>
        </div>
      </div>
      <div class="mk-book">
        <div class="fx-h">Sell orders</div>
        {book.length === 0 && <div class="fx-dim mk-book-empty">None today</div>}
        {book.slice(0, 5).map((l) => (
          <div key={l.id} class={`mk-book-row${l.player ? ' is-mine' : ''}`}>
            <Amount kind="gold" n={l.price} />
            <span class="fx-num">×{fmt(l.count)}</span>
            <span class="mk-seller">{l.player ? 'Your listing' : l.seller}</span>
          </div>
        ))}
      </div>
      <div class="fx-tabs mk-modes" role="tablist">
        {(['buy', 'sell', 'list'] as const).map((m) => (
          <button
            key={m}
            class="tab"
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setQty(1);
            }}
          >
            {m === 'buy' ? 'Buy' : m === 'sell' ? 'Sell now' : 'List'}
          </button>
        ))}
      </div>
      <div class="mk-form">{body}</div>
    </div>
  );
}

function ListingRow({
  l,
  day,
  now,
  onSelect,
  sel,
}: {
  l: PlayerListing;
  day: number;
  now: number;
  onSelect: () => void;
  sel: boolean;
}) {
  const ctx = useCtx();
  const g = GOODS[l.item];
  const st = listingStatus(l, day, now);
  const net = listingPayout(l.price, l.count).net;
  return (
    <div class={`fx-row mk-row mk-row--listing${sel ? ' is-sel' : ''}`} onClick={onSelect}>
      <Cell item={l.item} count={l.count * (g?.bundle ?? 1)} size={34} />
      <div class="fx-name" style={{ color: RARITY_COLOR[ITEMS[l.item]?.rarity ?? 'normal'] }}>
        {nameOf(l.item)}
      </div>
      <span class="fx-num">{l.count}</span>
      <Amount kind="gold" n={l.price} />
      <span class={`mk-status${st.waiting ? ' is-warn' : ''}`}>{st.text}</span>
      <Amount kind="gold" n={net} />
      <button
        class="btn sm"
        onClick={(e) => {
          e.stopPropagation();
          if (ctx && cancelListing(ctx.p, l.id)) {
            toast(`Listing withdrawn: ${nameOf(l.item)}`);
            commit();
          }
        }}
      >
        Cancel
      </button>
    </div>
  );
}

/** Panel `market`. */
export function MarketPanel() {
  const ctx = useCtx();
  const now = useNow(1000);
  const [tab, setTab] = useState<Tab>('browse');
  const [cat, setCat] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState<string | null>('leap_crystal');
  useEffect(() => {
    if (ctx && refreshMarket(ctx.a)) commit();
  }, []);
  if (!ctx) return null;
  const { c, a } = ctx;
  const day = a.day;
  const listings = playerListings(a);
  const unread = unreadCount(a);

  let rows: TradeGood[] = [];
  if (tab === 'browse') rows = searchGoods(query, cat, sub);
  else if (tab === 'sell')
    rows = searchGoods(query, null, null).filter((g) => countOf(c, g.id) > 0);
  const mode: Mode = tab === 'browse' ? 'buy' : tab === 'sell' ? 'sell' : 'list';
  const countIn = (catId: string, subId?: string) => searchGoods('', catId, subId ?? null).length;

  return (
    <Window
      title="Market"
      width="min(1120px, 96vw)"
      height="min(700px, 88vh)"
      className="fx-win fx-market"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'browse'}
            onClick={() => setTab('browse')}
          >
            Browse
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'sell'}
            onClick={() => setTab('sell')}
          >
            Sell
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'listings'}
            onClick={() => setTab('listings')}
          >
            My listings{' '}
            {listings.length > 0 && <span class="fx-badge is-gold">{listings.length}</span>}
          </button>
        </div>
        <input
          class="mk-search"
          type="search"
          placeholder="Search the market…"
          value={query}
          onInput={(e) => setQuery(e.currentTarget.value)}
        />
        <span class="fx-spacer" />
        <button class="btn sm mk-mailbtn" onClick={() => openPanel('mail')}>
          Mailbox {unread > 0 && <span class="fx-badge">{unread}</span>}
        </button>
        <Wallet a={a} c={c} />
      </div>
      <div class="mk-body">
        <nav class="mk-tree fx-scroll" aria-label="Categories">
          <button
            class={`mk-cat${!cat ? ' is-sel' : ''}`}
            onClick={() => {
              setCat(null);
              setSub(null);
              setQuery('');
              if (tab === 'listings') setTab('browse');
            }}
          >
            All goods <span>{countIn('')}</span>
          </button>
          {MARKET_CATEGORIES.map((k) => (
            <div key={k.id} class="mk-cat-group">
              <button
                class={`mk-cat${cat === k.id && !sub ? ' is-sel' : ''}`}
                onClick={() => {
                  setCat(k.id);
                  setSub(null);
                  setQuery('');
                  if (tab === 'listings') setTab('browse');
                }}
              >
                {k.name} <span>{countIn(k.id)}</span>
              </button>
              {cat === k.id &&
                k.subs.map((s) => (
                  <button
                    key={s.id}
                    class={`mk-sub${sub === s.id ? ' is-sel' : ''}`}
                    onClick={() => {
                      setSub(s.id);
                      setQuery('');
                    }}
                  >
                    {s.name} <span>{countIn(k.id, s.id)}</span>
                  </button>
                ))}
            </div>
          ))}
          <div class="mk-tree-foot">
            <span class="fx-h">Market day {day}</span>
            <p>Prices shift every day. Sell orders restock at dawn.</p>
          </div>
        </nav>
        <section class="mk-list">
          {tab === 'listings' ? (
            <>
              <div class="mk-row mk-row--listing mk-thead">
                <span />
                <span>Item</span>
                <span>Bundles</span>
                <span>Price</span>
                <span>Status</span>
                <span>Net</span>
                <span />
              </div>
              <div class="fx-scroll mk-rows">
                {listings.length === 0 ? (
                  <Empty title="No active listings">
                    Select something you own and choose List. Buyers arrive over time; proceeds come
                    by mail, less a 5% fee.
                  </Empty>
                ) : (
                  listings.map((l) => (
                    <ListingRow
                      key={l.id}
                      l={l}
                      day={day}
                      now={now}
                      sel={sel === l.item}
                      onSelect={() => setSel(l.item)}
                    />
                  ))
                )}
              </div>
              <div class="mk-list-foot fx-dim">
                {listings.length}/{MAX_PLAYER_LISTINGS} listings
              </div>
            </>
          ) : (
            <>
              <div class={`mk-row mk-thead${tab === 'sell' ? ' mk-row--sell' : ''}`}>
                <span />
                <span>Item</span>
                <span>{tab === 'sell' ? 'Owned' : 'Bundle'}</span>
                <span>Price</span>
                <span>Today</span>
                <span>{tab === 'sell' ? 'Bid' : 'Lowest'}</span>
                <span>{tab === 'sell' ? 'Bundles' : 'Supply'}</span>
              </div>
              <div class="fx-scroll mk-rows">
                {rows.length === 0 &&
                  (tab === 'sell' ? (
                    <Empty title="Nothing to sell">
                      You carry nothing the market trades. Gather materials, or pick up battle items
                      and gems.
                    </Empty>
                  ) : (
                    <Empty title="No matches">Try another search or category.</Empty>
                  ))}
                {rows.map((g) => {
                  const def = ITEMS[g.id];
                  const mid = midPrice(g.id, day);
                  const book = orderBook(a, g.id).filter((l) => !l.player);
                  const owned = countOf(c, g.id);
                  return (
                    <div
                      key={g.id}
                      class={`fx-row mk-row${tab === 'sell' ? ' mk-row--sell' : ''}${sel === g.id ? ' is-sel' : ''}`}
                      onClick={() => setSel(g.id)}
                    >
                      <Cell item={g.id} size={34} count={tab === 'sell' ? owned : undefined} />
                      <div class="fx-name" style={{ color: RARITY_COLOR[def?.rarity ?? 'normal'] }}>
                        {def?.name ?? g.id}
                      </div>
                      <span class="fx-num">
                        {tab === 'sell' ? fmt(owned) : `×${fmt(g.bundle)}`}
                      </span>
                      <Amount kind="gold" n={mid} />
                      <Delta now={mid} before={midPrice(g.id, day - 1)} />
                      {tab === 'sell' ? (
                        <Amount kind="gold" n={bidPrice(g.id, day)} />
                      ) : book[0] ? (
                        <Amount kind="gold" n={book[0].price} />
                      ) : (
                        <span class="fx-dim">—</span>
                      )}
                      <span class="fx-num">
                        {tab === 'sell'
                          ? fmt(Math.floor(owned / g.bundle))
                          : fmt(book.reduce((s, l) => s + l.count, 0))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
        <aside class="mk-detail fx-scroll">
          {sel && GOODS[sel] ? (
            <Detail key={sel} id={sel} initialMode={mode} />
          ) : (
            <Empty title="Select an item">
              Pick a good to see its price history and order book.
            </Empty>
          )}
        </aside>
      </div>
      <TipLayer />
    </Window>
  );
}
